import { from, of } from 'rxjs';
import {
  catchError,
  map,
  takeUntil,
  startWith,
  delay,
  filter,
  mergeMap,
  debounceTime
} from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { teladocApi, authToken } from '@td/api';
import {
  SUBMIT_PRESCRIPTION_FORM_SUCCESS,
  SUBMIT_MANUAL_PRESCRIPTIONS_SUCCESS,
  REVOKE_PROVIDER_ORDER_SUCCESS,
  UPDATE_PROVIDER_ORDER_SUCCESS,
  DELETE_PROVIDER_ORDER_SUCCESS
} from 'medication_service_ui';

import {
  FETCH_CLINICAL_MEDICATIONS,
  FETCH_MEDICATION_LIST_REQUEST,
  FETCH_MEDICATION_LIST_CANCEL
} from './action-types';

import {
  fetchMedicationListSuccess,
  fetchMedicationListFail,
  fetchMedicationListStart,
  fetchClinicalMedications,
  fetchClinicalMedicationsSuccess,
  fetchClinicalMedicationsStart,
  fetchClinicalMedicationsFail
} from './actions';

const v4DrugsPath = (term, regionCode = 'us') =>
  `/v4/drugs?term=${encodeURIComponent(term)}&region_code=${encodeURIComponent(regionCode)}`;

const v4MhdSelfReportedDrugSearchPath = (term, regionCode = 'us') =>
  `/v4/mhd_self_reported_drug_search?term=${encodeURIComponent(term)}&region_code=${encodeURIComponent(regionCode)}`;

const isEmrNewMedicationEnabled = () => window.FEATURE_TOGGLES.emr_new_medication;

const fetchClinicalMedRequest = payload => {
  const { url, params, token } = payload;
  return from(teladocApi.get(url, params, token));
};

export const refetchClinicalMedEpic = (action$, state$) =>
  action$.pipe(
    ofType(
      SUBMIT_PRESCRIPTION_FORM_SUCCESS,
      SUBMIT_MANUAL_PRESCRIPTIONS_SUCCESS,
      REVOKE_PROVIDER_ORDER_SUCCESS,
      UPDATE_PROVIDER_ORDER_SUCCESS,
      DELETE_PROVIDER_ORDER_SUCCESS),
    // The writes are not present in the read yet even though the endpoints have responded ok
    delay(1000),
    mergeMap(() => {
      const { settings: { memberParams: { memberId } } } = state$.value;

      return of(fetchClinicalMedications({ memberId }));
    }));

export const fetchClinicalMedEpic = action$ =>
  action$.pipe(
    ofType(FETCH_CLINICAL_MEDICATIONS),
    filter(({ payload: { params: { memberId } } }) => !!memberId),
    mergeMap(action =>
      fetchClinicalMedRequest(action.payload).pipe(
        map(response => fetchClinicalMedicationsSuccess(response)),
        takeUntil(action$.pipe(ofType('FETCH_CLINICAL_MEDICATION_CANCEL'))),
        startWith(fetchClinicalMedicationsStart()),
        catchError(error =>
          of(fetchClinicalMedicationsFail(error)))
      )
    ));

const buildFetchMedicationListRequest = (payload, state$) => {
  const { term } = payload;
  const {
    settings: { drugSearchAvailable, regionCode }
  } = state$.value;

  if (drugSearchAvailable) {
    return from(teladocApi.get(
      isEmrNewMedicationEnabled() ? v4MhdSelfReportedDrugSearchPath(term, regionCode) : v4DrugsPath(term, regionCode),
      {},
      authToken.get()
    ));
  } else {
    throw new Error('Drug search is not available');
  }
};

export const fetchMedicationListEpic = (action$, state$) =>
  action$.pipe(
    ofType(FETCH_MEDICATION_LIST_REQUEST),
    debounceTime(350),
    mergeMap(action =>
      buildFetchMedicationListRequest(action.payload, state$).pipe(
        map(response => fetchMedicationListSuccess(response)),
        takeUntil(action$.pipe(ofType(FETCH_MEDICATION_LIST_CANCEL))),
        startWith(fetchMedicationListStart()),
        catchError(error => of(fetchMedicationListFail(error)))
      )
    )
  );
